import { Controller } from "@hotwired/stimulus";
import { errorDetail, getJSON, sendJSON } from "../api.js";
import { detectPii } from "../pii.js";

// Wizard dodawania leada (personel z core.add_lead) na osobnym widoku
// /management/leady/nowy/. 2 kroki: krok 1 = typ leada (firma/osoba);
// krok 2 = cztery sekcje widoczne naraz:
//   01 Dane podstawowe (wymagane: phone + voivodeship combobox + subcategories ≥1;
//      dla osoby dodatkowo: name + email)
//   02 Dane firmowe (ukrywana dla osoba) LUB Dane osoby (ukrywana dla firma)
//   Dofinansowanie (wspólna dla obu rodzajów)
//   03 Podkategorie i cena (combobox multi + opcjonalna cena + price-hint)
//   04 Zgoda (RODO)
// Pola geograficzne i podkategorie obsługuje combobox_controller; wizard słucha
// zdarzenia "combobox:change" dla podkategorii → odświeża hint cenowy.
// Payload idzie JEDNYM POST-em na /api/leads/ (wszystko w transakcji backendu).
export default class extends Controller {
  static targets = [
    "step",
    "step1Chip",
    "step2Chip",
    "prevBtn",
    "nextBtn",
    "submitBtn",
    "error",
    "success",
    "kindInput",
    "companySection",
    "personSection",
    "isEmployedSelect",
    "employedSection",
    "unemployedSection",
    "emailInput",
    "emailReq",
    "noteInput",
    "noteError",
    "priceInput",
    "priceHint",
    "usedFundingSelect",
    "fundingYearInput",
    "tagChipsContainer",
    "tagChipsTemplate",
  ];

  static values = {
    url: String,         // /api/leads/
    priceHintUrl: String, // /api/leads/price-hint/
  };

  static PRICE_HINT_MAX = 6;

  // Tagi per podkategoria, zbierane ze zdarzeń "tag-chips:change" (Task 3
  // kontroler). Klucz = subcategory id jako string (tak przychodzi z
  // data-tag-chips-subcategory-value), wartość = tablica nazw tagów.
  // Payload czyta tę mapę przy submit (patrz #payload / placements).
  #tagsBySubcat = new Map();

  connect() {
    this.index = 0;
    this.#show();
    this.kindChanged();
    // Nasłuchuj zmian podkategorii z comboboxa → odśwież hinty cenowe + bloki tag-chips
    this.onSubcatChange = (e) => {
      if (e.detail.name !== "subcategories") return;
      const pks = Array.isArray(e.detail.value) ? e.detail.value : [];
      this.#updatePriceHints(pks);
      this.#reconcileTagChips(pks);
    };
    window.addEventListener("combobox:change", this.onSubcatChange);
    // Nasłuchuj zmian tagów per podkategoria (dispatch z tag-chips_controller.js)
    this.onTagChipsChange = (e) => {
      const { subcategory, names } = e.detail || {};
      if (subcategory === undefined || subcategory === null) return;
      this.#tagsBySubcat.set(String(subcategory), Array.isArray(names) ? names : []);
    };
    window.addEventListener("tag-chips:change", this.onTagChipsChange);
  }

  disconnect() {
    window.removeEventListener("combobox:change", this.onSubcatChange);
    window.removeEventListener("tag-chips:change", this.onTagChipsChange);
  }

  next() {
    if (!this.#validateCurrent()) return;
    this.index = Math.min(this.index + 1, this.stepTargets.length - 1);
    this.#show();
  }

  prev() {
    this.index = Math.max(this.index - 1, 0);
    this.#show();
  }

  // Krok 1: typ leada (firma/osoba) steruje widocznością sekcji firmowej i osobowej.
  // Dla osoby: e-mail staje się wymagany (+ gwiazdka w labelce).
  kindChanged() {
    const isOsoba = this.#kind() === "osoba";
    if (this.hasCompanySectionTarget) this.companySectionTarget.hidden = isOsoba;
    if (this.hasPersonSectionTarget) this.personSectionTarget.hidden = !isOsoba;
    if (this.hasEmailInputTarget) this.emailInputTarget.required = isOsoba;
    if (this.hasEmailReqTarget) this.emailReqTarget.hidden = !isOsoba;
  }

  // Sekcja osoby: is_employed steruje widocznością pól zatrudnienia / urzędu pracy.
  isEmployedChanged() {
    if (!this.hasIsEmployedSelectTarget) return;
    const val = this.isEmployedSelectTarget.value;
    const showEmployed = val === "true";
    const showUnemployed = val === "false";
    if (this.hasEmployedSectionTarget) this.employedSectionTarget.hidden = !showEmployed;
    if (this.hasUnemployedSectionTarget) this.unemployedSectionTarget.hidden = !showUnemployed;
  }

  // Dofinansowanie: rok aktywny tylko gdy used_funding=true (wspólne dla obu rodzajów).
  fundingChanged() {
    if (!this.hasUsedFundingSelectTarget || !this.hasFundingYearInputTarget) return;
    const used = this.usedFundingSelectTarget.value === "true";
    this.fundingYearInputTarget.disabled = !used;
    if (!used) this.fundingYearInputTarget.value = "";
  }

  async submit(event) {
    event.preventDefault();
    if (!this.#validateCurrent()) return;
    // Sprawdzenie PII w notatce przy submit.
    if (this.hasNoteInputTarget) {
      const hits = detectPii(this.noteInputTarget.value);
      if (hits.length) {
        if (this.hasNoteErrorTarget) {
          this.noteErrorTarget.textContent = `Usuń dane kontaktowe z notatki (wykryto: ${hits.join(", ")}).`;
          this.noteErrorTarget.hidden = false;
        }
        return;
      }
      if (this.hasNoteErrorTarget) this.noteErrorTarget.hidden = true;
    }
    const payload = this.#payload();
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasSuccessTarget) this.successTarget.hidden = true;
    const res = await sendJSON("POST", this.urlValue, payload);
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;
    if (!res.ok) {
      if (this.hasErrorTarget) {
        this.errorTarget.textContent = errorDetail(res.data, "Nie udało się dodać leada.");
        this.errorTarget.hidden = false;
      }
      return;
    }
    // Sukces: komunikat + link powrotu; BEZ auto-redirectu.
    if (this.hasSuccessTarget) this.successTarget.hidden = false;
    this.#resetWizard();
  }

  // --- prywatne ---

  // Odświeżenie hintów cenowych dla wybranych podkategorii (max PRICE_HINT_MAX).
  // Etykiety pobierane z kontrolera combobox (podkategorie) przez Stimulus API.
  async #updatePriceHints(pks) {
    if (!this.hasPriceHintTarget) return;
    if (!pks.length) {
      this.priceHintTarget.textContent = "";
      return;
    }
    const entries = pks.slice(0, this.constructor.PRICE_HINT_MAX);
    const comboEl = this.element.querySelector('[data-combobox-name-value="subcategories"]');
    const comboCtrl = comboEl
      ? this.application.getControllerForElementAndIdentifier(comboEl, "combobox")
      : null;
    const parts = [];
    for (const pk of entries) {
      const item = comboCtrl?.selected?.find((s) => s.value == pk);
      const label = item?.label ?? String(pk);
      let data;
      try {
        data = await getJSON(`${this.priceHintUrlValue}?subcategory=${pk}`);
      } catch {
        continue;
      }
      parts.push(
        data.price_pln == null || data.price_pln === undefined
          ? `${label} — brak cennika`
          : `${label} — ${data.price_pln} zł`,
      );
    }
    this.priceHintTarget.textContent = parts.length
      ? `Ceny z cennika (puste pole = cennik): ${parts.join(" · ")}`
      : "";
  }

  // Reconciluje bloki tag-chips z bieżącym wyborem podkategorii: dodaje blok
  // (klon <template>, bez innerHTML) dla każdego nowo wybranego id, usuwa blok
  // dla odznaczonego id, zostawia bez zmian bloki nadal wybranych podkategorii
  // (chipsy wpisane przez usera nie giną). Etykieta bloku = label z comboboxa.
  #reconcileTagChips(pks) {
    if (!this.hasTagChipsContainerTarget || !this.hasTagChipsTemplateTarget) return;
    const ids = pks.map((pk) => String(pk));
    const existingRows = new Map(
      [...this.tagChipsContainerTarget.children].map((row) => [row.dataset.subcategoryId, row]),
    );

    // Usuń bloki odznaczonych podkategorii + ich zebrane tagi.
    for (const [id, row] of existingRows) {
      if (!ids.includes(id)) {
        row.remove();
        this.#tagsBySubcat.delete(id);
      }
    }

    const comboEl = this.element.querySelector('[data-combobox-name-value="subcategories"]');
    const comboCtrl = comboEl
      ? this.application.getControllerForElementAndIdentifier(comboEl, "combobox")
      : null;

    // Dodaj bloki nowo wybranych podkategorii (kolejność jak w pks).
    for (const id of ids) {
      if (existingRows.has(id)) continue;
      const match = comboCtrl?.selected?.find((s) => String(s.value) === id);
      const label = match?.label ?? id;

      const fragment = this.tagChipsTemplateTarget.content.cloneNode(true);
      const row = fragment.querySelector("[data-tagchips-row]");
      if (!row) continue;
      // Row === element kontrolera tag-chips (data-controller="tag-chips" na tym samym węźle).
      row.dataset.subcategoryId = id;
      row.setAttribute("data-tag-chips-subcategory-value", id);

      const labelEl = row.querySelector("[data-tagchips-row-label]");
      if (labelEl) labelEl.textContent = label;

      this.tagChipsContainerTarget.appendChild(row);
    }
  }

  #kind() {
    const checked = this.kindInputTargets.find((input) => input.checked);
    return checked ? checked.value : "";
  }

  #show() {
    this.stepTargets.forEach((step, i) => {
      step.hidden = i !== this.index;
    });
    // Aktualizacja wizualnych chipów kroków
    if (this.hasStep1ChipTarget) {
      this.step1ChipTarget.className =
        this.index === 0 ? "ls-step is-active" : "ls-step is-done";
      this.step1ChipTarget.querySelector(".ls-step__dot").textContent =
        this.index === 0 ? "1" : "✓";
    }
    if (this.hasStep2ChipTarget) {
      this.step2ChipTarget.className =
        this.index >= 1 ? "ls-step is-active" : "ls-step";
    }
  }

  // Walidacja pól bieżącego kroku. Sprawdza natywne kontrolki przez constraint
  // validation API, a wymagane comboboxy — przez obecność hidden inputów.
  // Pomija kontrolki wewnątrz ukrytych kontenerów (hidden=true na przodku).
  #validateCurrent() {
    const step = this.stepTargets[this.index];
    const controls = step.querySelectorAll(
      "input:not([type=hidden]):not([type=radio]), select, textarea",
    );
    for (const control of controls) {
      if (control.disabled) continue;
      if (this.#isInsideHidden(control, step)) continue;
      if (!control.reportValidity()) return false;
    }
    // Walidacja radio w kroku 1
    const radios = step.querySelectorAll('input[type="radio"]');
    if (radios.length) {
      const names = new Set([...radios].map((r) => r.name));
      for (const name of names) {
        const group = [...step.querySelectorAll(`input[type="radio"][name="${name}"]`)];
        if (!group.some((r) => r.checked)) {
          group[0]?.reportValidity();
          return false;
        }
      }
    }
    // Walidacja wymaganych comboboxów (data-combobox-required-value="true")
    for (const combo of step.querySelectorAll('[data-combobox-required-value="true"]')) {
      const name = combo.getAttribute("data-combobox-name-value");
      const hidden = combo.querySelector(`input[type="hidden"][name="${name}"]`);
      if (!hidden || !hidden.value) {
        const inp = combo.querySelector('[data-combobox-target="input"]');
        if (inp && !inp.hidden) {
          inp.setCustomValidity("Pole wymagane");
          inp.reportValidity();
          inp.setCustomValidity("");
        }
        return false;
      }
    }
    return true;
  }

  // Sprawdza czy element jest wewnątrz ukrytego kontenera (aż do granicy step).
  // Używane przez #validateCurrent(), by pomijać pola w sekcjach hidden.
  #isInsideHidden(el, boundary) {
    let cur = el.parentElement;
    while (cur && cur !== boundary) {
      if (cur.hidden) return true;
      cur = cur.parentElement;
    }
    return false;
  }

  // Buduje payload POST-a. Podkategorie: N hidden inputów o nazwie "subcategories"
  // (combobox multi) — zbierane jako id-ki (string, kolejność wyboru), potem
  // zamieniane na payload.placements (patrz niżej): jedna pozycja na
  // podkategorię, z tagami z #tagsBySubcat (puste = cena podstawowa).
  // Pola firmowe tylko dla firma; pola osobowe tylko dla osoba.
  // Dofinansowanie wspólne dla obu rodzajów.
  // Consent: tylko gdy komplet channel+clause_version+granted_at.
  #payload() {
    const form = this.element.querySelector("form");
    const raw = {};
    const subcatIds = [];
    for (const input of form.querySelectorAll("[name]")) {
      if (input.disabled) continue;
      if (input.type === "radio" && !input.checked) continue;
      if (input.value === "") continue;
      if (input.name === "subcategories") {
        subcatIds.push(input.value);
        continue;
      }
      raw[input.name] = input.value;
    }

    const payload = { ...raw };
    const isCompany = raw.kind === "firma";

    // Pola wyłącznie firmowe (dofinansowanie jest wspólne — poniżej)
    const companyOnlyFields = [
      "nip",
      "employees_contract_count",
      "employees_mandate_count",
      "training_contract_count",
      "training_mandate_count",
    ];

    // Pola wyłącznie osobowe
    const personOnlyFields = [
      "name",
      "is_employed",
      "employment_type",
      "employment_industry",
      "registered_at_labour_office",
      "labour_office_name",
      "has_disability_certificate",
    ];

    if (isCompany) {
      // Firma: usuń wszystkie pola osobowe z payloadu
      for (const field of personOnlyFields) delete payload[field];
    } else {
      // Osoba: usuń pola firmowe
      for (const field of companyOnlyFields) delete payload[field];
      // Warunkowe pola zatrudnienia — wykluczaj gałąź niewidoczną
      const empVal = raw.is_employed;
      if (empVal === "true") {
        payload.is_employed = true;
        delete payload.registered_at_labour_office;
        delete payload.labour_office_name;
      } else if (empVal === "false") {
        payload.is_employed = false;
        delete payload.employment_type;
        delete payload.employment_industry;
        if ("registered_at_labour_office" in payload) {
          payload.registered_at_labour_office =
            payload.registered_at_labour_office === "true";
        }
      } else {
        // is_employed nieokreślone — usuń wszystkie warunkowe pola zatrudnienia
        delete payload.is_employed;
        delete payload.employment_type;
        delete payload.employment_industry;
        delete payload.registered_at_labour_office;
        delete payload.labour_office_name;
      }
      // Orzeczenie — konwersja string→boolean
      if ("has_disability_certificate" in payload) {
        payload.has_disability_certificate =
          payload.has_disability_certificate === "true";
      }
    }

    // Dofinansowanie — wspólne dla firmy i osoby
    if ("used_funding" in payload) {
      payload.used_funding = payload.used_funding === "true";
      if (!payload.used_funding) delete payload.funding_year;
    }

    if (subcatIds.length > 0) {
      // Jedna pozycja na wybraną podkategorię; tags puste = cena podstawowa.
      payload.placements = subcatIds.map((id) => ({
        subcategory: Number(id),
        tags: this.#tagsBySubcat.get(String(id)) ?? [],
      }));
    }

    // Consent: usuń płaskie pola, zbuduj zagnieżdżony obiekt gdy komplet.
    delete payload.consent_channel;
    delete payload.consent_clause_version;
    delete payload.consent_granted_at;
    delete payload.consent_expires_at;
    if (raw.consent_channel && raw.consent_clause_version && raw.consent_granted_at) {
      payload.consent = {
        channel: raw.consent_channel,
        clause_version: raw.consent_clause_version,
        granted_at: raw.consent_granted_at,
      };
      if (raw.consent_expires_at) payload.consent.expires_at = raw.consent_expires_at;
    }

    return payload;
  }

  #resetWizard() {
    this.element.querySelectorAll("form").forEach((form) => form.reset());
    // Reset comboboxów — clearAll() czyści wybory, chipy i hidden inputy
    for (const el of this.element.querySelectorAll('[data-controller~="combobox"]')) {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, "combobox");
      if (ctrl) ctrl.clearAll();
    }
    if (this.hasFundingYearInputTarget) this.fundingYearInputTarget.disabled = true;
    if (this.hasNoteErrorTarget) this.noteErrorTarget.hidden = true;
    if (this.hasPriceHintTarget) this.priceHintTarget.textContent = "";
    // Reset pod-sekcji zatrudnienia do stanu domyślnego (obie ukryte)
    if (this.hasIsEmployedSelectTarget) this.isEmployedChanged();
    this.kindChanged();
    this.index = 0;
    this.#show();
  }
}
